import { api } from '~/api/client.js';
import { lastStarted } from '~/store/lastStarted.js';
import {
  getLocalGame,
  isResumable,
  resumableGame,
  type LocalGameRecord,
} from '~/store/localGames.js';
import type { FaceLook } from './faceOrder.js';
import { chooseResumable, localResumable, onlineResumable, type ResumableGame } from './resume.js';

/**
 * How long the server gets to say whether an online game is waiting. Rails
 * gives the game service 1 s, and a second more covers the trip on a slow
 * phone. A connection that stalls rather than fails would otherwise hold back
 * a local game the device found at once.
 */
export const ONLINE_LOOKUP_MS = 2_000;

/** The server did not answer, which is not the same as "no game". */
const UNREACHED = Symbol('unreached');

/**
 * Ask the device and the server what the Resume door should offer.
 *
 * The two candidates are read at once. The local one is the game the "last
 * game started" marker names, or, with no marker, the newest unfinished local
 * game. The online one is whatever Rails says this player is seated in right
 * now. `chooseResumable` picks between them.
 *
 * Every failure reads as "no candidate" on its own side. A server that cannot
 * be reached, or does not answer within `ONLINE_LOOKUP_MS`, still leaves a
 * local game offered, and a device whose database will not open still leaves
 * an online game offered. The one exception is `showing`: an online game
 * already on the door stays there when the server cannot be reached, so one
 * dropped poll does not take the door away. A replaceable read: the hook that
 * calls it keeps the latest answer and drops the rest.
 */
export async function findResumable({
  playerId,
  online,
  now = Date.now(),
  showing = null,
  timeoutMs = ONLINE_LOOKUP_MS,
  you,
}: {
  readonly playerId: string | null;
  /** False with no session to ask the server with. */
  readonly online: boolean;
  readonly now?: number;
  /** The game the door shows now, if any. */
  readonly showing?: ResumableGame | null;
  readonly timeoutMs?: number;
  /** The player's own face, for the door's head (`useOwnFace`). */
  readonly you?: FaceLook | undefined;
}): Promise<ResumableGame | null> {
  const marker = lastStarted(playerId);
  const [record, answer] = await Promise.all([
    localCandidate(playerId, marker === null ? null : marker.kind === 'local' ? marker.id : ''),
    online
      ? api.activeGame(AbortSignal.timeout(timeoutMs)).catch((): typeof UNREACHED => UNREACHED)
      : Promise.resolve(null),
  ]);
  if (answer === UNREACHED && showing?.kind === 'online') return showing;
  const active = answer === UNREACHED ? null : answer;
  return chooseResumable({
    marker,
    local: record === null ? null : localResumable(record, you),
    localStartedAt: record?.startedAt ?? null,
    online: active === null ? null : onlineResumable(active, playerId, now, you),
    onlineStartedAt: active?.game.created_at ?? null,
  });
}

/**
 * The local game in the running.
 *
 * `markedId` is the local game the marker names, `''` when the marker names an
 * online game (so no local game is in the running), or null with no marker.
 */
async function localCandidate(
  playerId: string | null,
  markedId: string | null,
): Promise<LocalGameRecord | null> {
  if (markedId === '') return null;
  if (markedId === null) return resumableGame(playerId);
  const record = await getLocalGame(markedId);
  if (record === null || !isResumable(record)) return null;
  return mayResume(record, playerId) ? record : null;
}

/**
 * Whether this player may pick a stored game back up.
 *
 * A marker is per player, but the record says whose game it is, so the answer
 * is the record's owner. One record has none: a game started on a launch with
 * no network, before `/me` answered, is stamped by nobody. Who owns a record is
 * settled once and never guessed later (`claimUnownedGames`), so it stays
 * unowned. It is still this player's to resume when their own marker names it,
 * because only this device writes that marker, while it plays as them.
 */
export function mayResume(record: LocalGameRecord, playerId: string | null): boolean {
  if (record.playerId === playerId) return true;
  if (record.playerId !== '' || playerId === null) return false;
  const marker = lastStarted(playerId);
  return marker?.kind === 'local' && marker.id === record.id;
}
